import { ImagingApiService } from '@data-access/geospatial/imaging';
import {
  PanoramaImageStation,
  PlanarImageStation,
  StationsCapabilityProvider,
} from '@trimble/web3d-providers';
import { getLogger } from '@trimble/logging';
import { ViewModeCapabilityCoordinator } from '@mobile-mapping/web3d';
import { Accessor, createEffect, on } from 'solid-js';

import { patchStationWithTemplateUrl } from './stations-processor';

const logger = getLogger();

/**
 * Maps (runId, cameraName) → API coordinates needed to fetch tileset URLs.
 * Multiple runs share camera names but use different datasets, and each
 * camera within a run has its own dataset, so both dimensions are needed.
 */
export type TilesetPrefetchContext = Record<
  string,
  Record<string, { tenantTrn: string; datasetTrn: string }>
>;

/**
 * Number of stations before/after current to check for missing tileset URLs.
 * When any uncached station is detected in ±WINDOW, a batch fetch is triggered.
 * A larger window triggers the batch earlier, giving more "buffer" stations
 * for the API to respond before the user navigates into uncached territory.
 */
const TILESET_PREFETCH_WINDOW = 20;

/**
 * Extra stations to fetch beyond the display window in each direction.
 * When any uncached station is detected in the display window, we fetch
 * ±(WINDOW + FETCH_AHEAD) to reduce the number of batch API calls.
 * Example: WINDOW=10, FETCH_AHEAD=10 → check ±10, fetch ±20.
 * The user navigates ~10 stations before the next batch is triggered.
 * Keeping the total per batch moderate (~40 stations) ensures the API
 * responds in ≈1 s rather than 2+ s for larger payloads.
 */
const TILESET_FETCH_AHEAD = 10;

/**
 * Whether the prefetch window wraps around list boundaries.
 * Set to `true` if the UI allows navigating from the last station back to the
 * first (or vice-versa).  When `false`, indices are clamped so the prefetch
 * only covers reachable stations in each direction.
 */
const ALLOW_WRAPPING = false;

/**
 * Per-camera set of image IDs that currently have an in-flight batch request.
 * Shared across all panes so multiple effect runs never issue duplicate requests.
 * Old requests are allowed to complete (they're fetching nearby, useful stations).
 */
const prefetchInFlightIds = new Map<string, Set<string>>();

/** Minimum number of samples needed to calculate velocity. */
const MOMENTUM_MIN_SAMPLES = 2;
/** Milliseconds → seconds conversion factor. */
const MS_PER_SECOND = 1000;

/** Velocity (stations/s) at which fetch-ahead doubles. */
const MOMENTUM_VELOCITY_SCALE = 5;
/** Maximum multiplier applied to TILESET_FETCH_AHEAD based on velocity. */
const MOMENTUM_MAX_EXTRA_MULTIPLIER = 2;

/**
 * Tracks station navigation velocity to adaptively widen the prefetch window.
 * When the user switches stations rapidly, we fetch farther ahead so tiles
 * are already cached by the time they reach those stations.
 */
const navigationMomentum = {
  /** Recent (timestamp, stationIndex) samples. */
  samples: [] as Array<{ time: number; index: number }>,
  /** Maximum samples to retain. */
  maxSamples: 6,
  /** Sliding window for velocity calculation (ms). */
  windowMs: 3000,
  /**
   * Record a station navigation event and return the current velocity
   * (stations per second) over the sliding window.
   */
  recordAndGetVelocity(stationIndex: number): number {
    const now = Date.now();
    this.samples.push({ time: now, index: stationIndex });

    // Evict samples outside the time window
    const cutoff = now - this.windowMs;
    while (this.samples.length > 0 && this.samples[0].time < cutoff) {
      this.samples.shift();
    }
    // Cap to maxSamples
    while (this.samples.length > this.maxSamples) {
      this.samples.shift();
    }

    const sampleCount = this.samples.length;
    if (sampleCount < MOMENTUM_MIN_SAMPLES) return 0;

    const oldest = this.samples[0];
    const newest = this.samples[sampleCount - 1];
    const dtSeconds = (newest.time - oldest.time) / MS_PER_SECOND;
    if (dtSeconds === 0) return 0;

    return Math.abs(newest.index - oldest.index) / dtSeconds;
  },
};

const PREWARM_MAX_CONCURRENCY = 6;

const PREWARM_MAX_QUEUE = 64;

const prewarmRequested = new Set<string>();
const prewarmQueue: Array<{ url: string; run(): void }> = [];
let prewarmActive = 0;

let prewarmGeneration = 0;

export function resetTilesetPrefetchState(): void {
  prewarmRequested.clear();
  prewarmQueue.length = 0;
  prewarmActive = 0;
  prewarmGeneration++;
  prefetchInFlightIds.clear();
  navigationMomentum.samples.length = 0;
}

function pumpPreWarmQueue(): void {
  while (prewarmActive < PREWARM_MAX_CONCURRENCY && prewarmQueue.length > 0) {
    prewarmQueue.shift()?.run();
  }
}

function schedulePreWarmFetch(url: string): void {
  if (prewarmRequested.has(url)) return;
  prewarmRequested.add(url);

  const run = (): void => {
    const generation = prewarmGeneration;
    prewarmActive++;
    fetch(url)
      .catch(() => {
        if (generation === prewarmGeneration) prewarmRequested.delete(url);
      })
      .finally(() => {
        if (generation !== prewarmGeneration) return;
        prewarmActive--;
        pumpPreWarmQueue();
      });
  };

  prewarmQueue.push({ url, run });
  if (prewarmQueue.length > PREWARM_MAX_QUEUE) {
    const dropped = prewarmQueue.shift();
    if (dropped) prewarmRequested.delete(dropped.url);
  }
  pumpPreWarmQueue();
}

/**
 * Pre-warm the browser HTTP cache with root-level tiles so that when the
 * LOD analyzer's `requestTexture` calls `fetch()` for the same URL, it
 * returns instantly from cache instead of making a CDN round-trip (~200-500ms).
 *
 * - Both `PlanarLodAnalyzer` and `LodAnalyzer` use `FileImageLoader.getTexture()`
 *   which internally calls `fetch(url)`.  Pre-warming with the same URL populates
 *   the shared browser HTTP cache.
 * - Planar images have 1 root tile at z=0, x=0, y=0.
 * - Panoramas have 2 root tiles (west face: z=0, x=0, y=0; east face: z=0, x=1, y=0).
 */
function preWarmRootTiles(templateUrl: string): void {
  // Both PlanarModel and PanoramaModel create TWO LOD analyzers (left/right
  // or west/east halves).  Each has its own root tile at level 0.
  //   Left  / West  root: z=0, x=0, y=0
  //   Right / East  root: z=0, x=1, y=0
  // Pre-downloading both ensures the LOD analyzer's `fetch()` in
  // `requestTexture` gets an instant cache hit for both halves.
  const leftRootUrl = templateUrl
    .replace('{z}', '0')
    .replace('{x}', '0')
    .replace('{y}', '0');
  const rightRootUrl = templateUrl
    .replace('{z}', '0')
    .replace('{x}', '1')
    .replace('{y}', '0');

  schedulePreWarmFetch(leftRootUrl);
  schedulePreWarmFetch(rightRootUrl);
}

export interface TilesetPrefetchDeps {
  readonly stationsProvider: Accessor<StationsCapabilityProvider | undefined>;
  readonly imagingApi: ImagingApiService;
  readonly viewModeCapCoord: Accessor<
    ViewModeCapabilityCoordinator | undefined
  >;
  readonly tilesetPrefetchContext: Accessor<TilesetPrefetchContext>;
  /** Resolved name of the currently active/displayed camera. */
  readonly activeCameraName: Accessor<string | undefined>;
}

/**
 * Prefetch tileset URLs into the provider's cache (non-reactive Map).
 * When the batch finishes, bumps cacheVersion once so the loading effect retries.
 * Provider's stationList is never mutated — no array churn, no flickering.
 */
export function addTilesetPrefetchEffect(deps: TilesetPrefetchDeps): void {
  const {
    stationsProvider,
    imagingApi,
    viewModeCapCoord,
    tilesetPrefetchContext,
    activeCameraName,
  } = deps;

  createEffect(
    on(
      [
        () => stationsProvider(),
        () =>
          viewModeCapCoord()
            ?.stationNavigationController()
            ?.selectedStationIndex() ?? null,
        () => tilesetPrefetchContext(),
      ],
      ([provider, selectedStationIndex, context]) => {
        if (provider == null) return;
        const activeCamName = activeCameraName();

        const items = provider.items();
        const cache = provider.tilesetCache;

        // Sort cameras so the active camera's batch HTTP request fires first.
        const sortedCameras = [...items].sort((a, b) => {
          const aActive = a.cameraName === activeCamName ? 0 : 1;
          const bActive = b.cameraName === activeCamName ? 0 : 1;
          return aActive - bActive;
        });

        // Compute velocity once per effect run — station navigation is global
        // (all cameras share the same selectedStationIndex), so recording once
        // avoids inflating samples with per-camera duplicates.
        const velocity = navigationMomentum.recordAndGetVelocity(
          selectedStationIndex ?? 0
        );
        const velocityMultiplier =
          1 +
          Math.min(
            velocity / MOMENTUM_VELOCITY_SCALE,
            MOMENTUM_MAX_EXTRA_MULTIPLIER
          );
        const adaptiveFetchAhead = Math.ceil(
          TILESET_FETCH_AHEAD * velocityMultiplier
        );

        sortedCameras.forEach((camera) => {
          const cameraName = camera.cameraName;

          const stationList = camera.stationList;
          const n = stationList.length;
          if (n === 0) return;

          // Lazily create the in-flight set for this camera
          let inFlight = prefetchInFlightIds.get(cameraName);
          if (!inFlight) {
            inFlight = new Set<string>();
            prefetchInFlightIds.set(cameraName, inFlight);
          }

          const effectiveStationIndex =
            selectedStationIndex != null ? selectedStationIndex : n > 1 ? 1 : 0;

          // Phase 1: Check if anything in the display window needs a tileset
          const needsTileset = (
            s: NonNullable<(typeof stationList)[number]>
          ): boolean => {
            if (cache.has(s.station.id)) return false;
            if (inFlight!.has(s.station.id)) return false;
            if (s.kind === 'planar' && s.station.tilesUrl !== '') return false;
            if (
              s.kind === 'panorama' &&
              s.station.tilesetInfo?.tilesetUrl !== undefined
            )
              return false;
            return true;
          };

          let hasUncachedInWindow = false;
          const checkedIndices = new Set<number>();
          for (
            let offset = -TILESET_PREFETCH_WINDOW;
            offset <= TILESET_PREFETCH_WINDOW;
            offset++
          ) {
            let idx: number;
            if (ALLOW_WRAPPING) {
              idx = (((effectiveStationIndex + offset) % n) + n) % n;
            } else {
              idx = effectiveStationIndex + offset;
              if (idx < 0 || idx >= n) continue;
            }
            if (checkedIndices.has(idx)) continue;
            checkedIndices.add(idx);
            const s = stationList[idx];
            if (s && needsTileset(s)) {
              hasUncachedInWindow = true;
              break;
            }
          }

          if (!hasUncachedInWindow) return;

          // Phase 2: Fetch the extended range so we get a full chunk ahead
          const fetchRange = TILESET_PREFETCH_WINDOW + adaptiveFetchAhead;
          type StationToPrefetch = {
            idx: number;
            imageId: string;
            station: NonNullable<(typeof stationList)[number]>;
          };
          const stationsToPrefetch: StationToPrefetch[] = [];
          const visitedIndices = new Set<number>();
          for (let offset = -fetchRange; offset <= fetchRange; offset++) {
            let idx: number;
            if (ALLOW_WRAPPING) {
              idx = (((effectiveStationIndex + offset) % n) + n) % n;
            } else {
              idx = effectiveStationIndex + offset;
              if (idx < 0 || idx >= n) continue;
            }
            if (visitedIndices.has(idx)) continue;
            visitedIndices.add(idx);
            const s = stationList[idx];
            if (!s) continue;
            if (!needsTileset(s)) continue;
            stationsToPrefetch.push({
              idx,
              imageId: s.station.id,
              station: s,
            });
          }

          if (stationsToPrefetch.length === 0) return;

          // Prioritize current station: move it to the front
          const currentIdx = stationsToPrefetch.findIndex(
            (s) => s.idx === effectiveStationIndex
          );
          if (currentIdx > 0) {
            const [current] = stationsToPrefetch.splice(currentIdx, 1);
            stationsToPrefetch.unshift(current);
          }

          // Group stations by runId — each run×camera maps to a different
          // dataset, so the API call must target the correct dataset per run.
          const byRun = new Map<
            string,
            {
              ctx: { tenantTrn: string; datasetTrn: string };
              stations: StationToPrefetch[];
            }
          >();
          for (const entry of stationsToPrefetch) {
            const runId = entry.station.runId;
            const runCtx = context[runId]?.[cameraName];
            if (!runCtx) continue;
            let group = byRun.get(runId);
            if (!group) {
              group = { ctx: runCtx, stations: [] };
              byRun.set(runId, group);
            }
            group.stations.push(entry);
          }

          if (byRun.size === 0) return;

          const capturedInFlight = inFlight;

          // One API call per run (dataset).
          // Mark in-flight per group so only stations with valid context are
          // tracked — stations without context are left unmarked and will be
          // retried on the next effect run once context becomes available.
          for (const [, { ctx, stations }] of byRun) {
            const imageIds = stations.map((s) => s.imageId);
            for (const id of imageIds) {
              capturedInFlight.add(id);
            }
            imagingApi
              .getTilesetUrlsQuery(ctx.tenantTrn, ctx.datasetTrn, imageIds)
              .then((imageUrls) => {
                const urlMap = new Map<string, string>();
                for (const imageUrl of imageUrls) {
                  if (imageUrl.id && imageUrl.tilesetTemplateUrl) {
                    urlMap.set(imageUrl.id, imageUrl.tilesetTemplateUrl);
                  }
                }

                for (const templateUrl of urlMap.values()) {
                  preWarmRootTiles(templateUrl);
                }

                const cacheEntries: Array<{
                  imageId: string;
                  station: PanoramaImageStation | PlanarImageStation;
                }> = [];
                for (const { imageId, station } of stations) {
                  const templateUrl = urlMap.get(imageId);
                  if (!templateUrl) continue;
                  const patched = patchStationWithTemplateUrl(
                    station,
                    templateUrl
                  );
                  if (patched) {
                    cacheEntries.push({ imageId, station: patched });
                  }
                }

                if (cacheEntries.length > 0) {
                  provider.addToCacheAndNotify(cameraName, cacheEntries);
                }
              })
              .catch((error: unknown) => {
                logger.warn(
                  `Tileset prefetch failed for camera "${cameraName}" (${imageIds.length} stations)`,
                  error
                );
              })
              .finally(() => {
                for (const id of imageIds) {
                  capturedInFlight.delete(id);
                }
              });
          }
        });
      }
    )
  );
}
